import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Calculator, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

// ₹ per sqft ranges by scope × tier — deliberately wide so we set expectations,
// not final quotes. The consultation always precedes the actual BOQ.
const RATES = {
  "Full Home Interiors":       { Standard: [1200, 1600], Premium: [1700, 2400], Luxury: [2600, 4200] },
  "Modular Kitchen":            { Standard: [1600, 2200], Premium: [2400, 3200], Luxury: [3400, 5500] },
  "Wardrobes":                  { Standard: [1400, 1800], Premium: [1900, 2600], Luxury: [2800, 4200] },
  "Kitchen + Wardrobes":        { Standard: [1500, 2100], Premium: [2200, 3000], Luxury: [3200, 4800] },
  "Turnkey Commercial":         { Standard: [1800, 2400], Premium: [2600, 3600], Luxury: [3800, 6000] },
  "Office Interior":            { Standard: [1600, 2200], Premium: [2400, 3200], Luxury: [3400, 5500] },
  "Restaurant / Café Interior": { Standard: [2000, 2800], Premium: [3000, 4200], Luxury: [4400, 7000] },
  "Retail / Shop Interior":     { Standard: [1500, 2100], Premium: [2300, 3200], Luxury: [3400, 5200] },
};
const SCOPES = Object.keys(RATES);
const TIERS = ["Standard", "Premium", "Luxury"];

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

export default function Estimate() {
  const [scope, setScope] = useState(SCOPES[0]);
  const [tier, setTier] = useState("Premium");
  const [sqft, setSqft] = useState(650);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "Kolkata" });
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const range = useMemo(() => {
    const [lo, hi] = RATES[scope][tier];
    return [lo * sqft, hi * sqft];
  }, [scope, tier, sqft]);

  const isCommercialScope = /commercial|office|restaurant|retail/i.test(scope);

  const captureLead = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone so we can share the detailed estimate.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/consultations", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city || "Kolkata",
        property_type: isCommercialScope ? "Other Commercial" : "2 BHK",
        scope,
        budget: `Estimator: ₹${fmtINR(range[0])} – ₹${fmtINR(range[1])}`,
        message: `Budget Estimator submission — ${sqft} sqft · ${tier} tier · ${scope}. Rough range ₹${fmtINR(range[0])} – ₹${fmtINR(range[1])}.`,
        source: "Budget Estimator",
      });
      trackEvent("estimator_lead_submitted", { scope, tier, sqft, low: range[0], high: range[1] });
      setDone(true);
      toast.success("Detailed estimate on its way!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const chip = (active) =>
    `px-5 py-2.5 text-sm border transition-colors ${active ? "bg-[#143D63] text-[#F9F8F6] border-[#143D63]" : "bg-white text-[#143D63] border-[#143D63]/25 hover:border-[#143D63]"}`;

  return (
    <main data-testid="estimator-page" className="pt-32 pb-28 relative z-10 bg-[#F9F8F6]">
      <Seo
        title="Interior Design Cost Estimator | Kolkata — ZEBO"
        description="Get an instant rough budget for full-home interiors, modular kitchens, wardrobes and turnkey commercial fit-outs in Kolkata. Configure sqft, scope and finish tier."
        path="/estimate"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2">
            <Calculator size={14} /> Instant Estimator
          </p>
          <h1 className="font-serif text-6xl sm:text-7xl font-light tracking-tight text-[#143D63] leading-[0.95]">
            Know your interior budget in 30 seconds.
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-lg">
            Pick your scope, tell us your carpet area, choose a finish tier and see a live cost range. Share your details and we&apos;ll email a detailed line-item estimate within a day.
          </p>
          <div className="mt-8 border-l-2 border-[#F8C21C] pl-5 py-3 text-sm text-[#143D63]/70">
            Numbers are indicative rupee-per-sqft ranges for Kolkata. The final BOQ is created after your consultation and site visit.
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="border border-[#143D63]/15 bg-white p-8 space-y-8">
            <div>
              <p className="overline text-[#143D63]/60 mb-3">Scope</p>
              <div className="flex flex-wrap gap-2">
                {SCOPES.map((s) => (
                  <button key={s} type="button" data-testid={`estimator-scope-${s.replace(/\W+/g, "-").toLowerCase()}`}
                    className={chip(scope === s)} onClick={() => setScope(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="overline text-[#143D63]/60 mb-3">Finish tier</p>
              <div className="flex flex-wrap gap-2">
                {TIERS.map((t) => (
                  <button key={t} type="button" data-testid={`estimator-tier-${t.toLowerCase()}`}
                    className={chip(tier === t)} onClick={() => setTier(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="overline text-[#143D63]/60">Carpet area</p>
                <p data-testid="estimator-sqft-label" className="font-semibold text-[#143D63]">{sqft.toLocaleString("en-IN")} sqft</p>
              </div>
              <input
                data-testid="estimator-sqft"
                type="range"
                min={150}
                max={5000}
                step={50}
                value={sqft}
                onChange={(e) => setSqft(Number(e.target.value))}
                className="w-full accent-[#143D63]"
              />
              <div className="flex justify-between text-xs text-[#143D63]/50 mt-2">
                <span>150 sqft</span>
                <span>5,000 sqft</span>
              </div>
            </div>

            <div data-testid="estimator-output" className="bg-[#143D63] text-[#F9F8F6] px-6 py-8">
              <p className="overline text-[#F8C21C] mb-3">Estimated range</p>
              <p className="font-serif text-3xl sm:text-4xl font-light">
                ₹ {fmtINR(range[0])} <span className="text-[#F9F8F6]/60">to</span> ₹ {fmtINR(range[1])}
              </p>
              <p className="text-[#F9F8F6]/60 text-xs mt-3">{scope} · {tier} finish · {sqft.toLocaleString("en-IN")} sqft in Kolkata</p>
            </div>

            {!done ? (
              <form onSubmit={captureLead} className="grid sm:grid-cols-2 gap-3">
                <input data-testid="estimator-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" required />
                <input data-testid="estimator-phone" placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" required />
                <input data-testid="estimator-email" type="email" placeholder="you@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none sm:col-span-1" required />
                <input data-testid="estimator-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none" />
                <button data-testid="estimator-submit" type="submit" disabled={saving}
                  className="sm:col-span-2 flex items-center justify-center gap-2 bg-[#F8C21C] text-[#143D63] px-8 py-3.5 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-60">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  Email me the detailed estimate
                </button>
                <p className="sm:col-span-2 text-xs text-[#143D63]/50">We&apos;ll never spam. This becomes a lead in our CRM so a designer can send a line-item BOQ.</p>
              </form>
            ) : (
              <div data-testid="estimator-success" className="border border-[#F8C21C] bg-[#FFF7DA] p-6 text-center">
                <Sparkles className="text-[#8B0000] mx-auto mb-3" size={30} />
                <h3 className="font-serif text-2xl text-[#143D63] mb-2">Thanks, {form.name.split(" ")[0]}!</h3>
                <p className="text-[#143D63]/70 mb-5 text-sm">A ZEBO designer will send your detailed estimate within a business day.</p>
                <button onClick={() => nav("/consultation")} className="inline-flex items-center gap-2 bg-[#143D63] text-[#F9F8F6] px-6 py-3 text-sm font-bold hover:bg-[#F8C21C] hover:text-[#143D63] transition-colors">
                  Or book a free consultation now <ArrowUpRight size={14} />
                </button>
              </div>
            )}
          </motion.div>

          <p className="mt-6 text-sm text-[#143D63]/60">
            Want to try the AI room designer first? <Link to="/studio" className="text-[#8B0000] link-underline font-semibold">Open ZEBO Studio</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
